// Number Sense v2 — scale-type-first trainer data + logic.
//
// v1 (numberSenseData.js) hardcodes one curriculum unit per key, so every key
// re-teaches the same pattern. v2 flips that: each *scale type* (major, natural
// minor, pentatonics…) is one unit defined by its interval formula, and every
// drill is generated dynamically for whichever tonic the player picks — the
// same "template × key" approach chordCraft.js uses for progressions.
//
// Pure data + generators live here (with colocated tests); the only audio
// dependency is playNote, for the one helper that schedules a scale ascent.

import { playNote } from "./keyboardPlan";

/* ============================================================
   Pitch spelling
   ============================================================ */

export const SHARP_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
export const FLAT_NAMES = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];

const wrapPc = (pc) => ((pc % 12) + 12) % 12;

export const noteName = (pc, useFlats) =>
  (useFlats ? FLAT_NAMES : SHARP_NAMES)[wrapPc(pc)];

// The on-screen Piano (numberSenseUi) names its keys with sharps, so answers
// checked against tapped keys must be sharp-spelled even in flat keys.
export const sharpName = (pc) => SHARP_NAMES[wrapPc(pc)];

const LETTERS = ["C", "D", "E", "F", "G", "A", "B"];
const LETTER_PCS = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

// pc of a spelled name ("Bb", "E#", "F") — handles the enharmonic spellings
// letter-per-degree spelling produces, so options can be deduped by pitch.
export const pcOfName = (name) => {
  let pc = LETTER_PCS[name[0]];
  for (const ch of name.slice(1)) pc += ch === "#" ? 1 : ch === "b" ? -1 : 0;
  return wrapPc(pc);
};

/* ============================================================
   Tonics — 12 per family, circle-of-fifths display order.
   Scale degrees are spelled letter-per-degree (see spellScale);
   `useFlats` picks the chromatic table used for distractor
   neighbors and the rare double-accidental fallback.
   ============================================================ */

export const MAJOR_TONICS = [
  { name: "C", pc: 0, useFlats: false },
  { name: "G", pc: 7, useFlats: false },
  { name: "D", pc: 2, useFlats: false },
  { name: "A", pc: 9, useFlats: false },
  { name: "E", pc: 4, useFlats: false },
  { name: "B", pc: 11, useFlats: false },
  { name: "F#", pc: 6, useFlats: false },
  { name: "Db", pc: 1, useFlats: true },
  { name: "Ab", pc: 8, useFlats: true },
  { name: "Eb", pc: 3, useFlats: true },
  { name: "Bb", pc: 10, useFlats: true },
  { name: "F", pc: 5, useFlats: true },
];

export const MINOR_TONICS = [
  { name: "A", pc: 9, useFlats: false },
  { name: "E", pc: 4, useFlats: false },
  { name: "B", pc: 11, useFlats: false },
  { name: "F#", pc: 6, useFlats: false },
  { name: "C#", pc: 1, useFlats: false },
  { name: "G#", pc: 8, useFlats: false },
  { name: "Eb", pc: 3, useFlats: true },
  { name: "Bb", pc: 10, useFlats: true },
  { name: "F", pc: 5, useFlats: true },
  { name: "C", pc: 0, useFlats: true },
  { name: "G", pc: 7, useFlats: true },
  { name: "D", pc: 2, useFlats: true },
];

/* ============================================================
   Scale types — the units of v2. Everything a drill needs is
   derived from `semis` (offsets from the tonic) at question time.
   `triads` marks the 7-note types that support chord-quality
   questions (triads are stacked in-scale: 1-3-5 of the mode).
   ============================================================ */

export const SCALE_TYPES = [
  {
    id: "major",
    title: "the major scale",
    short: "major",
    gloss: "do-re-mi — where the numbers come from",
    semis: [0, 2, 4, 5, 7, 9, 11],
    degreeLabels: ["1", "2", "3", "4", "5", "6", "7"],
    family: "major",
    refKey: "C",
    altKey: "Eb",
    triads: true,
    romans: ["I", "ii", "iii", "IV", "V", "vi", "vii°"],
    teach: {
      intro:
        "the sound of do-re-mi, western music's home base. pop choruses, folk tunes, film scores that want to feel open and resolved. learn this one first, since every other scale gets described as a twist on it.",
      down: "major still sounds bright coming down, but landing on the tonic from above is a different skill than launching off it below — practice finding home, not just leaving it.",
    },
  },
  {
    id: "natural-minor",
    title: "the natural minor scale",
    short: "minor",
    gloss: "the sad twin — major's pattern, started on 6",
    semis: [0, 2, 3, 5, 7, 8, 10],
    degreeLabels: ["1", "2", "b3", "4", "5", "b6", "b7"],
    family: "minor",
    refKey: "A",
    altKey: "C",
    triads: true,
    romans: ["i", "ii°", "III", "iv", "v", "VI", "VII"],
    teach: {
      intro:
        "major's moodier twin, same shape started on scale degree 6. this is ballad minor, indie sad-song minor, the sound of missing someone without any extra drama.",
      down: "no raised leading tone here, so nothing pulls you back upward — natural minor falls just as easily as it climbs, the most balanced scale to drill both directions.",
    },
  },
  {
    id: "harmonic-minor",
    title: "the harmonic minor scale",
    short: "harmonic minor",
    gloss: "minor with a raised 7 — drama and a real V chord",
    semis: [0, 2, 3, 5, 7, 8, 11],
    degreeLabels: ["1", "2", "b3", "4", "5", "b6", "7"],
    family: "minor",
    refKey: "A",
    altKey: "D",
    triads: true,
    romans: ["i", "ii°", "III+", "iv", "V", "VI", "vii°"],
    teach: {
      intro:
        "natural minor with the 7th raised, so you get a real leading tone and an actual V chord to resolve to. that raise also stretches the gap between b6 and 7 into an augmented 2nd, the exotic hop behind flamenco runs and shred solos.",
      down: "that augmented 2nd shows up again on the way down, now falling instead of climbing — same exotic leap, opposite direction, and just as easy to sing flat.",
    },
  },
  {
    id: "melodic-minor",
    title: "the melodic minor scale",
    short: "melodic minor",
    gloss: "minor below, major on top — the smooth climb",
    semis: [0, 2, 3, 5, 7, 9, 11],
    degreeLabels: ["1", "2", "b3", "4", "5", "6", "7"],
    family: "minor",
    refKey: "A",
    altKey: "G",
    triads: true,
    romans: ["i", "ii", "III+", "IV", "V", "vi°", "vii°"],
    teach: {
      intro:
        "natural minor with both the 6th and 7th raised, smoothing out harmonic minor's awkward leap into something sleeker. jazz treats this as its default minor sound, moody on the bottom and bright on top.",
      down: "classical tradition famously drops back to natural minor coming down, a different scale each direction. this app drills it the jazz way instead — same raised notes up and down, no switch.",
    },
  },
  {
    id: "major-pentatonic",
    title: "the major pentatonic",
    short: "major pentatonic",
    gloss: "five notes, zero clunkers — campfire safe",
    semis: [0, 2, 4, 7, 9],
    degreeLabels: ["1", "2", "3", "5", "6"],
    family: "major",
    refKey: "G",
    altKey: "F",
    triads: false,
    teach: {
      intro:
        "major's five safest notes, half-steps removed so nothing can clash. country licks, singer-songwriter riffs, that instant sing-along brightness. training wheels that never really come off, since plenty of pros still reach for them.",
      down: "coming down, those wide gaps turn into easy falls — major pentatonic descending is the sound of a riff bouncing loosely back home instead of climbing to it.",
    },
  },
  {
    id: "minor-pentatonic",
    title: "the minor pentatonic",
    short: "minor pentatonic",
    gloss: "the riff machine — five notes that always land",
    semis: [0, 3, 5, 7, 10],
    degreeLabels: ["1", "b3", "4", "5", "b7"],
    family: "minor",
    refKey: "A",
    altKey: "E",
    triads: false,
    teach: {
      intro:
        "minor's five toughest notes, no half-steps to trip over. blues, rock, and hip-hop practically run on this scale, and almost any order of these notes sounds like a riff. learn it and you can solo over nearly anything.",
      down: "descending minor pentatonic is the classic guitar-solo move — wide, bendy steps tumbling back to the root, a lick winding down instead of climbing up.",
    },
  },
  {
    id: "blues",
    title: "the blues scale",
    short: "blues",
    gloss: "minor pentatonic plus the blue note",
    semis: [0, 3, 5, 6, 7, 10],
    degreeLabels: ["1", "b3", "4", "b5", "5", "b7"],
    family: "minor",
    refKey: "A",
    altKey: "C",
    triads: false,
    teach: {
      intro:
        "minor pentatonic plus one outlaw note, the b5, wedged between the 4th and 5th. that blue note is the wrong note that's right, all grit and tension, the sound of a guitar or harmonica crying a little.",
      down: "coming down, the b5 slides through like a passing tone between 5 and 4 — that little chromatic scoop is half of what makes blues phrasing sound like speech.",
    },
  },
];

export const scaleType = (id) => SCALE_TYPES.find((t) => t.id === id) || null;

export const tonicsFor = (type) =>
  type.family === "minor" ? MINOR_TONICS : MAJOR_TONICS;

export const tonicByName = (type, name) =>
  tonicsFor(type).find((t) => t.name === name) || null;

// "Bb major", "F# minor", "C blues" — the phrase drills use everywhere.
export const keyLabel = (type, tonicName) => `${tonicName} ${type.short}`;

/* ============================================================
   Derivations from the formula
   ============================================================ */

// Step sizes between neighbors, as friendly labels. 3 semitones (harmonic
// minor's 6→7 leap, pentatonic gaps) reads as "W+H".
export const STEP_LABELS = { 1: "H", 2: "W", 3: "W+H" };

export const stepPattern = (type) => {
  const gaps = [];
  const semis = [...type.semis, 12];
  for (let i = 1; i < semis.length; i += 1) gaps.push(semis[i] - semis[i - 1]);
  return gaps.map((g) => STEP_LABELS[g]);
};

export const patternString = (type) => stepPattern(type).join(" ");

// "b3" / "7" / "1" → its plain degree number, for letter arithmetic.
const degreeNumber = (label) => parseInt(label.replace(/[b#]/g, ""), 10);

// The scale spelled for a tonic — proper letter-per-degree spelling: degree n
// gets the tonic's letter plus (n-1), and the accidental falls out of the
// pitch (so A blues shows Eb for b5, F# major shows E# for 7). A degree that
// would need a double accidental (G# harmonic minor's F##) falls back to the
// tonic's chromatic table to stay readable on a piano.
export const spellScale = (type, tonicName) => {
  const t = tonicByName(type, tonicName);
  if (!t) return [];
  const li0 = LETTERS.indexOf(tonicName[0]);
  return type.semis.map((s, i) => {
    const pc = wrapPc(t.pc + s);
    const letter = LETTERS[(li0 + degreeNumber(type.degreeLabels[i]) - 1) % 7];
    const diff = wrapPc(pc - LETTER_PCS[letter]);
    if (diff === 0) return letter;
    if (diff === 1) return `${letter}#`;
    if (diff === 11) return `${letter}b`;
    return noteName(pc, t.useFlats);
  });
};

// Sharp-spelled pitch classes, for checking taps on the on-screen piano.
export const scaleSharpNotes = (type, tonicName) => {
  const t = tonicByName(type, tonicName);
  if (!t) return [];
  return type.semis.map((s) => sharpName(t.pc + s));
};

// Ascending midis from the octave of middle C, tonic to tonic (for playback
// and piano hints). Highest possible note is 60 + 11 + 12 = 83, inside the
// on-screen piano's C4–C6 range.
export const scaleMidis = (type, tonicName) => {
  const t = tonicByName(type, tonicName);
  if (!t) return [];
  const base = 60 + wrapPc(t.pc);
  return [...type.semis, 12].map((s) => base + s);
};

// The same run from the top tonic down — melodies fall as often as they rise.
export const scaleMidisDown = (type, tonicName) =>
  [...scaleMidis(type, tonicName)].reverse();

// Schedule an ascending run of midis (the "hear the scale" button). Returns
// the total duration in ms so callers can time their "playing" state.
export const playMidiSeq = (midis, beatMs = 300) => {
  midis.forEach((m, i) => playNote(m, beatMs * 1.6, (i * beatMs) / 1000));
  return midis.length * beatMs;
};

// The in-scale triad on a degree: 1-3-5 stacked from the scale itself. Note
// names come from the spelled scale, so a chip never disagrees with the
// scale row above it (SEMITONES knows the enharmonic spellings, so these
// names stay playable).
export const scaleTriad = (type, tonicName, degIndex) => {
  const t = tonicByName(type, tonicName);
  if (!t || !type.triads) return null;
  const n = type.semis.length;
  const off = (k) =>
    type.semis[(degIndex + k) % n] + (degIndex + k >= n ? 12 : 0);
  const semis = [off(0), off(2), off(4)];
  const third = semis[1] - semis[0];
  const fifth = semis[2] - semis[0];
  let quality = "major";
  if (fifth === 6) quality = "diminished";
  else if (fifth === 8) quality = "augmented";
  else if (third === 3) quality = "minor";
  const spelled = spellScale(type, tonicName);
  const names = [0, 2, 4].map((k) => spelled[(degIndex + k) % n]);
  const suffix = { major: "", minor: "m", diminished: "°", augmented: "+" }[quality];
  return {
    root: names[0],
    quality,
    symbol: `${names[0]}${suffix}`,
    notes: names,
    roman: type.romans ? type.romans[degIndex] : null,
  };
};

/* ============================================================
   Guided scale walk — the hands-on bridge between the type-level
   teach and drilling an unfamiliar key: build this key's scale on
   the piano one step at a time, following the W/H recipe. Each
   step carries the exact midi to press, its spelled name and
   degree, and the step size that gets you there from the last one.
   ============================================================ */

export const STEP_HINTS = {
  H: "a half step — the very next key, black or white",
  W: "a whole step — two keys up, and black keys count",
  "W+H": "a step and a half — three keys up",
};

export const scaleWalk = (typeId, keyName) => {
  const type = scaleType(typeId);
  if (!type) return null;
  const midis = scaleMidis(type, keyName);
  if (!midis.length) return null;
  const names = [...spellScale(type, keyName), spellScale(type, keyName)[0]];
  const labels = [...type.degreeLabels, type.degreeLabels[0]];
  const gaps = stepPattern(type);
  return {
    typeId,
    keyName,
    label: keyLabel(type, keyName),
    steps: midis.map((midi, i) => ({
      midi,
      name: names[i],
      degree: labels[i],
      step: i === 0 ? null : gaps[i - 1], // null = "find home" opening step
    })),
  };
};

// XP for finishing a key's first guided walk (scale or chord stack).
export const WALK_XP = 8;

/* ============================================================
   Guided chord stack — the chord twin of the scale walk: for each
   degree, build its triad by stacking 1-3-5 from the scale (root,
   skip a scale note, 3rd, skip, 5th), and watch the qualities
   fall out. Triad-bearing types only. Each round carries the
   exact midis to press, in stacking order, plus the reveal
   (symbol + quality) for when the triad completes.
   ============================================================ */

const ROLE_LABELS = ["root", "3rd", "5th"];

export const chordWalk = (typeId, keyName) => {
  const type = scaleType(typeId);
  if (!type || !type.triads) return null;
  const t = tonicByName(type, keyName);
  if (!t) return null;
  const n = type.semis.length;
  const base = 60 + wrapPc(t.pc);
  // The scale extended past the octave, so upper chord tones keep climbing.
  const ext = (k) => base + type.semis[k % n] + 12 * Math.floor(k / n);
  const rounds = type.semis.map((_, i) => {
    const triad = scaleTriad(type, keyName, i);
    let midis = [ext(i), ext(i + 2), ext(i + 4)];
    // Keep every tone on the two-octave piano (C4–C6): drop the whole triad
    // an octave when its top would run off the board.
    if (midis[2] > 84) midis = midis.map((m) => m - 12);
    return {
      degIndex: i,
      degree: type.degreeLabels[i],
      roman: triad.roman,
      symbol: triad.symbol,
      quality: triad.quality,
      tones: midis.map((midi, k) => ({
        midi,
        name: triad.notes[k],
        role: ROLE_LABELS[k],
      })),
    };
  });
  return { typeId, keyName, label: keyLabel(type, keyName), rounds };
};

/* ============================================================
   Inversions — the same triad with a different chord tone on the
   bottom. Voicings carry exact midis (which octave each tone sits
   in is the whole point), stacked closed from a bass anchored in
   the piano's bottom octave, so every voicing fits C4–C6.
   ============================================================ */

export const INVERSION_NAMES = ["root position", "1st inversion", "2nd inversion"];

// The triad on a degree, voiced in one inversion: 0 = root position (1-3-5),
// 1 = 1st (3-5-1), 2 = 2nd (5-1-3). Slash symbols name the new bass
// ("Dm/F"); root position keeps the plain symbol.
export const triadInversion = (typeId, keyName, degIndex, inv) => {
  const type = scaleType(typeId);
  if (!type || !type.triads) return null;
  const t = tonicByName(type, keyName);
  const triad = scaleTriad(type, keyName, degIndex);
  if (!t || !triad) return null;
  const n = type.semis.length;
  const pcs = [0, 2, 4].map((k) =>
    wrapPc(t.pc + type.semis[(degIndex + k) % n])
  );
  // chord-tone index of each voice, bottom to top
  const order = [inv % 3, (inv + 1) % 3, (inv + 2) % 3];
  const midis = [60 + pcs[order[0]]];
  for (let v = 1; v < 3; v += 1) {
    midis.push(midis[v - 1] + (wrapPc(pcs[order[v]] - pcs[order[v - 1]]) || 12));
  }
  const bass = triad.notes[order[0]];
  return {
    inv,
    invName: INVERSION_NAMES[inv],
    degIndex,
    degree: type.degreeLabels[degIndex],
    roman: triad.roman,
    quality: triad.quality,
    baseSymbol: triad.symbol,
    symbol: inv === 0 ? triad.symbol : `${triad.symbol}/${bass}`,
    bass,
    bassPc: pcs[order[0]],
    stack: order.map((ci, v) => ({
      midi: midis[v],
      name: triad.notes[ci],
      role: ROLE_LABELS[ci],
    })),
  };
};

// Sound a voicing as one chord — exact midis, so inversions actually invert
// (pitch-class playback would re-stack everything around one octave).
export const playMidiChord = (midis, durMs = 900, delaySec = 0) => {
  midis.forEach((m) => playNote(m, durMs, delaySec));
  return durMs;
};

/* ============================================================
   Guided flip walk — the inversion twin of the chord stack: take
   the three primary chords (I, IV, V — the ones songs leans on)
   through all three positions each. Build root position, then
   flip the bottom note up top, twice. Triad types only.
   ============================================================ */

export const INV_WALK_DEGREES = [0, 3, 4];

export const invWalk = (typeId, keyName) => {
  const type = scaleType(typeId);
  if (!type || !type.triads) return null;
  if (!tonicByName(type, keyName)) return null;
  const rounds = INV_WALK_DEGREES.map((d) => {
    const stages = [0, 1, 2].map((inv) => triadInversion(typeId, keyName, d, inv));
    return {
      degIndex: d,
      degree: type.degreeLabels[d],
      roman: stages[0].roman,
      symbol: stages[0].symbol,
      stages,
    };
  });
  return { typeId, keyName, label: keyLabel(type, keyName), rounds };
};

/* ============================================================
   Question generators — each returns one plain question object.
   Multiple-choice questions carry { prompt, correct, options };
   `build` carries target notes for the piano; `ear` carries midis.
   Every question is tagged with { typeId, keyName } so mastery
   can be updated per concept even in mixed-key sessions.
   ============================================================ */

const shuffle = (arr, rng = Math.random) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const pick = (arr, rng = Math.random) => arr[Math.floor(rng() * arr.length)];

// correct + (count-1) distinct distractors, shuffled.
const withDistractors = (correct, pool, count, rng) => {
  const wrong = shuffle(
    [...new Set(pool)].filter((x) => x !== correct),
    rng
  ).slice(0, count - 1);
  return shuffle([correct, ...wrong], rng);
};

// "from degree X up to Y is a …?" — drills the pattern itself.
export const qStep = (type, rng = Math.random) => {
  const gaps = stepPattern(type);
  const i = Math.floor(rng() * gaps.length);
  const labels = type.degreeLabels;
  const from = labels[i];
  const to = i + 1 < labels.length ? labels[i + 1] : `1 (top)`;
  const options = [...new Set(["W", "H", "W+H", gaps[i]])].slice(0, 3);
  return {
    type: "step",
    typeId: type.id,
    keyName: null,
    prompt: `in the ${type.short} scale, the step from ${from} up to ${to} is…`,
    correct: gaps[i],
    options: shuffle(options, rng),
    note: `${type.short}: ${patternString(type)}`,
  };
};

// "which one is this scale's pattern?" — distractors are real patterns of
// the other types, so the confusions are the musically meaningful ones.
export const qFullPattern = (type, rng = Math.random) => {
  const pool = SCALE_TYPES.map((t) => patternString(t));
  return {
    type: "fullpattern",
    typeId: type.id,
    keyName: null,
    prompt: `which step pattern builds the ${type.short} scale?`,
    correct: patternString(type),
    options: withDistractors(patternString(type), pool, 4, rng),
    note: `every ${type.short} scale uses it, whatever the tonic`,
  };
};

// Other scale tones (properly spelled) + chromatic neighbors — plausible
// wrong answers for note-naming questions. Deduped by pitch class, with the
// correct answer's pitch excluded entirely, so the options never offer two
// names for one key (correct "Eb" next to distractor "D#").
const noteDistractorPool = (type, tonicName, correct) => {
  const t = tonicByName(type, tonicName);
  const raw = [...spellScale(type, tonicName)];
  type.semis.forEach((s) => {
    raw.push(noteName(t.pc + s + 1, t.useFlats));
    raw.push(noteName(t.pc + s - 1, t.useFlats));
  });
  const seen = new Set([pcOfName(correct)]);
  const pool = [];
  raw.forEach((n) => {
    const pc = pcOfName(n);
    if (seen.has(pc)) return;
    seen.add(pc);
    pool.push(n);
  });
  return pool;
};

export const qScaleNote = (type, tonicName, rng = Math.random) => {
  const notes = spellScale(type, tonicName);
  const i = 1 + Math.floor(rng() * (notes.length - 1)); // skip the tonic — too easy
  const deg = type.degreeLabels[i];
  return {
    type: "scalenote",
    typeId: type.id,
    keyName: tonicName,
    prompt: `in ${keyLabel(type, tonicName)}, which note is ${deg}?`,
    correct: notes[i],
    options: withDistractors(
      notes[i],
      noteDistractorPool(type, tonicName, notes[i]),
      4,
      rng
    ),
    note: `${keyLabel(type, tonicName)}: ${notes.join(" ")}`,
  };
};

export const qDegree = (type, tonicName, rng = Math.random) => {
  const notes = spellScale(type, tonicName);
  const i = 1 + Math.floor(rng() * (notes.length - 1));
  return {
    type: "degree",
    typeId: type.id,
    keyName: tonicName,
    prompt: `in ${keyLabel(type, tonicName)}, ${notes[i]} is which degree?`,
    correct: type.degreeLabels[i],
    options: withDistractors(type.degreeLabels[i], type.degreeLabels, 4, rng),
    note: `${keyLabel(type, tonicName)}: ${notes.join(" ")}`,
  };
};

export const qNextNote = (type, tonicName, rng = Math.random, dir = "up") => {
  const notes = spellScale(type, tonicName);
  const down = dir === "down";
  // Descending runs read from the top tonic: 1 · b7 · b6 … — the note *below*
  // is its own skill, so the sequence (and the answer key) is the reverse walk.
  const seq = down ? [notes[0], ...[...notes.slice(1)].reverse()] : notes;
  const i = 1 + Math.floor(rng() * (seq.length - 1));
  const lead = seq.slice(Math.max(0, i - 3), i);
  return {
    type: "nextnote",
    typeId: type.id,
    keyName: tonicName,
    down,
    prompt: `${down ? "coming down" : "going up"} ${keyLabel(
      type,
      tonicName
    )}: ${lead.join(" · ")} — what's next?`,
    correct: seq[i],
    options: withDistractors(
      seq[i],
      noteDistractorPool(type, tonicName, seq[i]),
      4,
      rng
    ),
    note: `${keyLabel(type, tonicName)}${down ? " down" : ""}: ${seq.join(" ")}`,
  };
};

// "the triad the scale builds on b3 is …" — only for triad-bearing types.
export const qQuality = (type, tonicName, rng = Math.random) => {
  if (!type.triads) return null;
  const i = Math.floor(rng() * type.semis.length);
  const triad = scaleTriad(type, tonicName, i);
  const suffixes = ["", "m", "°", "+"];
  const options = withDistractors(
    triad.symbol,
    suffixes.map((s) => `${triad.root}${s}`),
    4,
    rng
  );
  return {
    type: "quality",
    typeId: type.id,
    keyName: tonicName,
    prompt: `in ${keyLabel(type, tonicName)}, the chord built on ${
      type.degreeLabels[i]
    } is…`,
    correct: triad.symbol,
    options,
    playNotes: triad.notes,
    note: `${triad.roman || type.degreeLabels[i]} in ${keyLabel(
      type,
      tonicName
    )} — ${triad.notes.join("–")}`,
  };
};

// Hear a run (up or down — you have to know it both ways), name the scale
// type — the one cross-type question.
export const qEar = (type, tonicName, rng = Math.random) => {
  const options = withDistractors(
    type.short,
    SCALE_TYPES.map((t) => t.short),
    4,
    rng
  );
  const down = rng() < 0.5;
  return {
    type: "ear",
    typeId: type.id,
    keyName: tonicName,
    down,
    prompt: "which scale type is this?",
    correct: type.short,
    options,
    playMidis: down
      ? scaleMidisDown(type, tonicName)
      : scaleMidis(type, tonicName),
    note: `that was ${keyLabel(type, tonicName)}${down ? ", coming down" : ""}`,
  };
};

// The finale: tap the whole scale on the piano (any octave).
export const qBuild = (type, tonicName) => ({
  type: "build",
  typeId: type.id,
  keyName: tonicName,
  label: keyLabel(type, tonicName),
  displayNotes: spellScale(type, tonicName),
  targetNotes: scaleSharpNotes(type, tonicName), // piano keys spell sharps
  playMidis: scaleMidis(type, tonicName),
  note: `${keyLabel(type, tonicName)}: ${spellScale(type, tonicName).join(" ")}`,
});

// Play a degree's triad on the piano (any octave) — the prompt gives only the
// degree, so naming the notes is the skill. Triad types only.
export const qBuildChord = (type, tonicName, rng = Math.random) => {
  if (!type.triads) return null;
  const i = Math.floor(rng() * type.semis.length);
  const triad = scaleTriad(type, tonicName, i);
  const t = tonicByName(type, tonicName);
  return {
    type: "build",
    chord: true,
    typeId: type.id,
    keyName: tonicName,
    label: `the ${type.degreeLabels[i]} chord in ${keyLabel(type, tonicName)}`,
    symbol: triad.symbol,
    displayNotes: triad.notes,
    targetNotes: [0, 2, 4].map((k) =>
      sharpName(t.pc + type.semis[(i + k) % type.semis.length])
    ),
    playNotes: triad.notes,
    note: `${triad.roman} in ${keyLabel(type, tonicName)} is ${triad.symbol} — ${triad.notes.join("–")}`,
  };
};

// Hear home (the 1 chord), then a mystery degree chord — name the degree.
// Context first, because a lone triad says nothing about its place in a key.
export const qChordEar = (type, tonicName, rng = Math.random) => {
  if (!type.triads) return null;
  const i = 1 + Math.floor(rng() * (type.semis.length - 1)); // never the tonic
  const triad = scaleTriad(type, tonicName, i);
  const home = scaleTriad(type, tonicName, 0);
  return {
    type: "chordear",
    typeId: type.id,
    keyName: tonicName,
    prompt: `in ${keyLabel(type, tonicName)}: hear the 1 chord, then a mystery chord — which degree is it?`,
    correct: type.degreeLabels[i],
    options: withDistractors(
      type.degreeLabels[i],
      type.degreeLabels.slice(1),
      4,
      rng
    ),
    playChords: [home.notes, triad.notes],
    note: `that was ${triad.roman} — ${triad.symbol}`,
  };
};

// Build a slash chord on the piano — the bass note is the answer that
// matters, so checking is pitch-classes + which note sits lowest. Root
// position stays chord-build's job; this drill always flips.
export const qBuildInversion = (type, tonicName, rng = Math.random) => {
  if (!type.triads) return null;
  const i = Math.floor(rng() * type.semis.length);
  const inv = 1 + Math.floor(rng() * 2);
  const v = triadInversion(type.id, tonicName, i, inv);
  return {
    type: "buildinv",
    typeId: type.id,
    keyName: tonicName,
    label: v.symbol,
    invName: v.invName,
    bass: v.bass,
    bassPc: v.bassPc,
    displayNotes: v.stack.map((s) => s.name),
    targetNotes: v.stack.map((s) => sharpName(s.midi)), // piano keys spell sharps
    playMidis: v.stack.map((s) => s.midi),
    note: `${v.symbol} is ${v.invName} — ${v.stack
      .map((s) => s.name)
      .join("–")} from the bottom up`,
  };
};

// Name the flip, both directions: see a stack (low → high), pick its slash
// name — or see the slash name, pick the stack. Distractors are the same
// chord's other two positions, so the confusion is always about the bass.
export const qNameInversion = (type, tonicName, rng = Math.random) => {
  if (!type.triads) return null;
  const i = Math.floor(rng() * type.semis.length);
  const all = [0, 1, 2].map((inv) => triadInversion(type.id, tonicName, i, inv));
  const v = all[Math.floor(rng() * 3)];
  const nameOf = (x) => `${x.symbol} — ${x.invName}`;
  const stackOf = (x) => x.stack.map((s) => s.name).join(" · ");
  const toStack = rng() < 0.5;
  return {
    type: "invname",
    typeId: type.id,
    keyName: tonicName,
    prompt: toStack
      ? `which stack (low to high) is ${nameOf(v)}?`
      : `${v.baseSymbol} stacked ${stackOf(v)}, low to high — which position is that?`,
    correct: toStack ? stackOf(v) : nameOf(v),
    options: shuffle(all.map(toStack ? stackOf : nameOf), rng),
    playMidis: v.stack.map((s) => s.midi),
    note: `${nameOf(v)}: ${stackOf(v)} — the bass is the ${
      v.stack[0].role
    }`,
  };
};

// Hear root position, then the same chord flipped (or not) — which position
// lands second? Root first as the reference, because an inversion is only
// audible against where the root ought to be.
export const qInvEar = (type, tonicName, rng = Math.random) => {
  if (!type.triads) return null;
  const i = Math.floor(rng() * type.semis.length);
  const inv = Math.floor(rng() * 3);
  const root = triadInversion(type.id, tonicName, i, 0);
  const v = triadInversion(type.id, tonicName, i, inv);
  return {
    type: "invear",
    typeId: type.id,
    keyName: tonicName,
    prompt: `hear ${root.symbol} in root position, then again — which position is the second one?`,
    correct: v.invName,
    options: [...INVERSION_NAMES],
    playMidiChords: [root.stack.map((s) => s.midi), v.stack.map((s) => s.midi)],
    note: `that was ${v.symbol} — ${v.invName}`,
  };
};

/* ============================================================
   Session builder — one Duolingo-style run. Two flavors:

   "mixed" (the default): pattern first, tap-the-scale last, a
   shuffled bit-of-everything middle, played with hearts — the
   run that levels a key toward its crown.

   Focused kinds (degrees / climb / chords / pattern): practice
   mode — no hearts, pick a length (10/15/20), grind one skill.
   Generators randomize per question, so a rerun deals a mix of
   fresh and familiar prompts.

   keyName "mix" rolls a fresh tonic per question in any kind.
   ============================================================ */

export const MIX_KEY = "mix";
export const SESSION_HEARTS = 3;
export const SESSION_LENGTHS = [10, 15, 20];
export const DEFAULT_LENGTH = 10;

export const DRILL_KINDS = [
  {
    id: "mixed",
    title: "mixed set",
    gloss: "a bit of everything, build-the-scale finale",
    hearts: true,
    group: "scale",
  },
  {
    id: "degrees",
    title: "degrees",
    gloss: "name the note ↔ name the degree",
    hearts: false,
    group: "scale",
  },
  {
    id: "climb",
    title: "next note",
    gloss: "up and down the scale — what comes next?",
    hearts: false,
    group: "scale",
  },
  {
    id: "pattern",
    title: "the recipe",
    gloss: "whole and half steps, cold",
    hearts: false,
    group: "scale",
  },
  // ---- the chords the scale grows (triad types only) ----
  {
    id: "chord-build",
    title: "build the chord",
    gloss: "play the triad a degree grows — any octave",
    hearts: false,
    group: "chords",
    needsTriads: true,
  },
  {
    id: "chords",
    title: "name the chord",
    gloss: "which chord does this degree grow?",
    hearts: false,
    group: "chords",
    needsTriads: true,
  },
  {
    id: "chord-ear",
    title: "chord ears",
    gloss: "hear 1, then a mystery chord — name the degree",
    hearts: false,
    group: "chords",
    needsTriads: true,
    needsAudio: true,
  },
  // ---- inversions: same notes, new bottom (triad types only) ----
  {
    id: "inv-build",
    title: "build the flip",
    gloss: "play the slash chord — right note on the bottom",
    hearts: false,
    group: "inversions",
    needsTriads: true,
  },
  {
    id: "inv-name",
    title: "name the flip",
    gloss: "slash name ↔ the stack, low to high",
    hearts: false,
    group: "inversions",
    needsTriads: true,
  },
  {
    id: "inv-ear",
    title: "flip ears",
    gloss: "hear root position, then a flip — which is it?",
    hearts: false,
    group: "inversions",
    needsTriads: true,
    needsAudio: true,
  },
];

export const drillKind = (id) => DRILL_KINDS.find((k) => k.id === id) || null;

// Chord-quality drills only exist for the triad-bearing 7-note types.
export const drillKindsFor = (type) =>
  DRILL_KINDS.filter((k) => !k.needsTriads || type.triads);

export const buildDrillSession = (
  typeId,
  keyName,
  { kind = "mixed", length = DEFAULT_LENGTH, canPlay = true, rng = Math.random } = {}
) => {
  const type = scaleType(typeId);
  const kindDef = drillKind(kind);
  if (!type || !kindDef) return null;
  if (kindDef.needsTriads && !type.triads) return null;
  const tonics = tonicsFor(type);
  const roll = () => (keyName === MIX_KEY ? pick(tonics, rng).name : keyName);

  let questions;
  if (kind === "degrees") {
    questions = Array.from({ length }, (_, i) =>
      i % 2 === 0 ? qScaleNote(type, roll(), rng) : qDegree(type, roll(), rng)
    );
  } else if (kind === "climb") {
    // Alternate directions — coming down is just as important as going up.
    questions = Array.from({ length }, (_, i) =>
      qNextNote(type, roll(), rng, i % 2 === 0 ? "up" : "down")
    );
  } else if (kind === "chords") {
    questions = Array.from({ length }, () => qQuality(type, roll(), rng));
  } else if (kind === "chord-build") {
    questions = Array.from({ length }, () => qBuildChord(type, roll(), rng));
  } else if (kind === "chord-ear") {
    questions = Array.from({ length }, () => qChordEar(type, roll(), rng));
  } else if (kind === "inv-build") {
    questions = Array.from({ length }, () => qBuildInversion(type, roll(), rng));
  } else if (kind === "inv-name") {
    questions = Array.from({ length }, () => qNameInversion(type, roll(), rng));
  } else if (kind === "inv-ear") {
    questions = Array.from({ length }, () => qInvEar(type, roll(), rng));
  } else if (kind === "pattern") {
    questions = Array.from({ length }, (_, i) =>
      i % 3 === 2 ? qFullPattern(type, rng) : qStep(type, rng)
    );
  } else {
    // mixed: opener + shuffled middle (cycled up to length) + build finale
    const opener = rng() < 0.5 ? qFullPattern(type, rng) : qStep(type, rng);
    const deal = [
      () => qScaleNote(type, roll(), rng),
      () => qDegree(type, roll(), rng),
      () => qNextNote(type, roll(), rng, rng() < 0.5 ? "up" : "down"),
      () => qStep(type, rng),
      () =>
        type.triads ? qQuality(type, roll(), rng) : qScaleNote(type, roll(), rng),
    ];
    const middle = [];
    const midCount = Math.max(3, length - 2 - (canPlay ? 1 : 0));
    for (let i = 0; i < midCount; i += 1) middle.push(deal[i % deal.length]());
    if (canPlay) middle.push(qEar(type, roll(), rng));
    questions = [opener, ...shuffle(middle, rng), qBuild(type, roll())];
  }

  return { typeId, keyName, kind, practice: !kindDef.hearts, questions };
};

/* ============================================================
   Progress: XP, streak, per-(type, key) mastery levels.
   mastery is a flat map { "major:Bb": { strength, seen, correct,
   last } } like v1's, with strength the 0–5 Duolingo-ish level.
   ============================================================ */

export const MASTERY_MAX = 5;

export const conceptId = (typeId, keyName) => `${typeId}:${keyName}`;

export const levelOf = (mastery, typeId, keyName) =>
  (mastery && mastery[conceptId(typeId, keyName)]?.strength) || 0;

// Fold one finished session into mastery: group results per concept, promote
// a concept its questions answered ≥80% right, demote one below 50%.
export const applySessionResults = (mastery, results, today) => {
  const next = { ...(mastery || {}) };
  const groups = {};
  results.forEach((r) => {
    if (!r.keyName) return; // pattern questions are key-less
    const id = conceptId(r.typeId, r.keyName);
    (groups[id] = groups[id] || []).push(r.correct);
  });
  Object.entries(groups).forEach(([id, marks]) => {
    const prev = next[id] || { strength: 0, seen: 0, correct: 0, last: "" };
    const acc = marks.filter(Boolean).length / marks.length;
    let strength = prev.strength;
    if (marks.length >= 2 && acc >= 0.8) strength = Math.min(MASTERY_MAX, strength + 1);
    else if (acc < 0.5) strength = Math.max(0, strength - 1);
    next[id] = {
      strength,
      seen: prev.seen + marks.length,
      correct: prev.correct + marks.filter(Boolean).length,
      last: today || prev.last,
    };
  });
  return next;
};

// XP for a finished session: base + perfect bonus (hearts untouched).
export const sessionXp = (hearts, maxHearts = SESSION_HEARTS) =>
  15 + (hearts === maxHearts ? 5 : 0);

// XP for a finished practice run (no hearts — reward showing up, bonus for a
// sharp run).
export const practiceXp = (accPct) => 10 + (accPct >= 90 ? 5 : 0);

/* ============================================================
   Drill records — per (type, key, drill kind): runs, best
   accuracy, and once accuracy tops out, speed. Times are always
   recorded on perfect (100%) runs, but the timer UI stays hidden
   until a drill has TIMER_UNLOCK_PERFECTS clean runs — new
   players never see a clock.
   ============================================================ */

// Perfect runs of one drill before its timer (and best-clean-time chase)
// becomes visible.
export const TIMER_UNLOCK_PERFECTS = 3;

export const drillStatId = (typeId, keyName, kind) =>
  `${typeId}:${keyName}:${kind}`;

export const drillStatOf = (stats, typeId, keyName, kind) =>
  (stats && stats[drillStatId(typeId, keyName, kind)]) || null;

export const recordDrillRun = (
  stats,
  typeId,
  keyName,
  kind,
  accPct,
  totalMs = 0
) => {
  const id = drillStatId(typeId, keyName, kind);
  const prev = (stats && stats[id]) || {
    runs: 0,
    best: 0,
    last: 0,
    perfects: 0,
    bestMs: 0,
    lastMs: 0,
  };
  const perfect = accPct >= 100 && totalMs > 0;
  return {
    ...(stats || {}),
    [id]: {
      runs: prev.runs + 1,
      best: Math.max(prev.best, accPct),
      last: accPct,
      perfects: (prev.perfects || 0) + (accPct >= 100 ? 1 : 0),
      // best/last clean time only ever compares perfect runs — a fast sloppy
      // run is not a record.
      bestMs: perfect
        ? prev.bestMs
          ? Math.min(prev.bestMs, totalMs)
          : totalMs
        : prev.bestMs || 0,
      lastMs: perfect ? totalMs : prev.lastMs || 0,
    },
  };
};

export const timerUnlocked = (stats, typeId, keyName, kind) => {
  const s = drillStatOf(stats, typeId, keyName, kind);
  return Boolean(s && (s.perfects || 0) >= TIMER_UNLOCK_PERFECTS);
};

// "1:42" / "0:07" for run times shown once the timer is unlocked.
export const fmtMs = (ms) => {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/* ============================================================
   Answer streak — consecutive correct answers across runs (and
   drills): the "27 in a row" that separates a lucky 100 from a
   reliable one. A pure fold so the page can persist it.
   ============================================================ */

export const foldAnswerStreak = (streak, results) => {
  let current = (streak && streak.current) || 0;
  let best = (streak && streak.best) || 0;
  results.forEach((r) => {
    current = r.correct ? current + 1 : 0;
    if (current > best) best = current;
  });
  return { current, best };
};

// Per-type rollup for the home cards: how many keys have any level / are
// crowned (maxed).
export const typeProgress = (mastery, type) => {
  const tonics = tonicsFor(type);
  let leveled = 0;
  let crowned = 0;
  tonics.forEach((t) => {
    const s = levelOf(mastery, type.id, t.name);
    if (s > 0) leveled += 1;
    if (s >= MASTERY_MAX) crowned += 1;
  });
  return { leveled, crowned, total: tonics.length };
};

/* ============================================================
   Teach slides — one short intro per scale type. Slide extras:
     pattern : render the W/H step chips
     scale   : { key } spell + play it in a key
     compare : { keys: [a, b] } same pattern, two tonics
     chords  : { key } the triads the scale builds (triad types)
   ============================================================ */

export const teachSlides = (type) => {
  const teach = type.teach || {};
  const slides = [
    {
      title: type.title,
      body:
        teach.intro ||
        `${type.gloss}. one pattern of steps — learn it once and you own it in all twelve keys.`,
      scale: { key: type.refKey },
    },
    {
      title: "the recipe",
      body: `every ${type.short} scale climbs the same ladder of whole steps (W) and half steps (H). start on any note, follow the recipe, and you can't miss.`,
      pattern: true,
    },
    {
      title: "coming down",
      body:
        teach.down ||
        `melodies fall as often as they rise — read the recipe backwards and know the note below, not just the one above.`,
      scale: { key: type.refKey, down: true },
    },
    {
      title: "same pattern, new tonic",
      body: `move the starting note and the pattern drags the right sharps and flats along with it. ${type.refKey} and ${type.altKey} are the same shape in different places.`,
      compare: { keys: [type.refKey, type.altKey] },
    },
  ];
  if (type.triads) {
    slides.push({
      title: "the chords it makes",
      body: `stack every other scale note and each degree grows its own triad — the qualities land the same way in every key, which is why numbers beat letters.`,
      chords: { key: type.refKey },
    });
  }
  slides.push({
    title: "drill it",
    body: `pick a key — any key — and run the drill, up and down. level a key up to ${MASTERY_MAX} to crown it, and use "mix it up" to drill them all at once.`,
  });
  return slides;
};
